import { useCallback, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '../lib/supabaseClient';
import { useRoundBuckets } from './useRoundBuckets';
import { isCupMatch } from '../lib/competition/cupRounds';
import {
  buildRecencyFlagMaps,
  recencyTriggerRoundLabel,
  type RecencyFlag,
  type RecencyFlagRow,
  type RecencyFlagsByMatch,
} from '../utils/recencyFlags';

const EMPTY_BY_MATCH: RecencyFlagsByMatch = new Map();
const EMPTY_FLAGS: Map<string, RecencyFlag> = new Map();

/** Query-key prefix shared with the realtime handlers' invalidations. */
export const OFFICIAL_RECENCY_QUERY_KEY_PREFIX = 'official-recency';

/**
 * Recency flags for a set of matches: officials whose last N assignments
 * (per-league window, same group + season) included a match involving either
 * team of the target match. Advisory only — errors resolve to no flags.
 */
export function useOfficialRecencyFlags(matchIds: string[]): { data: RecencyFlagsByMatch } {
  const sortedIds = useMemo(
    () => [...matchIds].filter((id) => id && !id.startsWith('temp-')).sort(),
    [matchIds]
  );
  const { data } = useQuery({
    queryKey: [OFFICIAL_RECENCY_QUERY_KEY_PREFIX, sortedIds],
    queryFn: async () => {
      const { data, error } = await supabase.rpc('get_official_recency_flags', {
        p_match_ids: sortedIds,
      });
      if (error) throw error;
      return buildRecencyFlagMaps((data ?? []) as RecencyFlagRow[]);
    },
    enabled: sortedIds.length > 0,
    staleTime: 60_000,
  });
  return { data: data ?? EMPTY_BY_MATCH };
}

/** Convenience single-match variant. */
export function useMatchRecencyFlags(matchId: string | null): { data: Map<string, RecencyFlag> } {
  const ids = useMemo(() => (matchId ? [matchId] : []), [matchId]);
  const { data } = useOfficialRecencyFlags(ids);
  return { data: (matchId && data.get(matchId)) || EMPTY_FLAGS };
}

/**
 * Tour du match déclencheur d'un avertissement de récence (null = match de championnat,
 * le texte garde « J{n} ») ; « … » tant que cup_rounds se charge. `target` = le match
 * ciblé (même groupe que le déclencheur).
 */
export function useRecencyRoundLabel(): (
  flag: RecencyFlag,
  target: { isExternalCompetition?: boolean | null; leagueGroup?: { kind?: string | null } | null } | null | undefined,
) => string | null {
  const { cupLabel } = useRoundBuckets();
  return useCallback(
    (flag, target) =>
      recencyTriggerRoundLabel(flag, {
        targetIsCup: !!target && isCupMatch(target),
        cupLabel: (round) => cupLabel(round),
      }),
    [cupLabel],
  );
}
